import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { SparqlManager } from "../../../helpers/SparqlManager"
import { getEncodedPathName, toAbsoluteUrl } from "./urls"

const QUERY_KEY = "ghosted-models" as const

type GhostedModelsConf = {
  sparqlManager: SparqlManager
  urls: string[]
}

export function useGhostedModels(
  conf: GhostedModelsConf
): UseQueryResult<ReadonlySet<string>> {
  return useQuery({
    enabled: conf.urls.length > 0,
    queryKey: [ QUERY_KEY, ...conf.urls ],
    queryFn: () => fetchGhostedModels(conf.sparqlManager, conf.urls),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })
}

async function fetchGhostedModels(
  sparqlManager: SparqlManager,
  urls: string[]
): Promise<ReadonlySet<string>> {
  const result = await sparqlManager.fetch(`
      # DEBT: Due to a bug in SparqlManager, a timestamp is necessary to properly invalidate it
      #${Date.now()}
      #nocache

      PREFIX uaf: <http://wistor.nl/upload/admin/uploadanyfile#>
      PREFIX ifcv4: <https://wistor.nl/ifc/v4#>

      SELECT DISTINCT ?url ?ghost WHERE {
          VALUES (?url) {
              ${urls.map(url => `("${url}")`).join("\n")}
          }

          ?file a uaf:UploadedFile ;
                uaf:filename ?relativeUrl .
          FILTER(STRENDS(?url, ?relativeUrl))

          OPTIONAL {
              ?file uaf:belongsTo ?convertedFrom .
              ?convertedFrom uaf:format "ifc" .
          }
          BIND(COALESCE(?convertedFrom, ?file) AS ?ifc)

          ?ifc ifcv4:displayConfig ?displayConfig .
          ?displayConfig ifcv4:ghost ?ghost .
      }
    `)

  if (result.isError) throw new Error("SparqlManager returned error")

  const ghostedUrls = new Set<string>()
  for (const binding of result.result.results.bindings) {
    if (binding.ghost?.value !== "true") continue
    const url = binding.url?.value
    if (!url) continue
    ghostedUrls.add(toAbsoluteUrl(getEncodedPathName(url)))
  }

  return ghostedUrls
}
